import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip
} from '@mui/material';
import { Person, Email, CheckCircle, Business } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();

  const infoCards = [
    {
      title: 'Role',
      value: user?.role || 'N/A',
      icon: <Person sx={{ fontSize: 40, color: '#1a56db' }} />,
      color: '#1a56db',
    },
    {
      title: 'Email',
      value: user?.email || 'N/A',
      icon: <Email sx={{ fontSize: 40, color: '#10b981' }} />,
      color: '#10b981',
    },
    {
      title: 'Status',
      value: user?.is_active ? 'Active' : 'Inactive',
      icon: <CheckCircle sx={{ fontSize: 40, color: user?.is_active ? '#10b981' : '#ef4444' }} />,
      color: user?.is_active ? '#10b981' : '#ef4444',
    },
    {
      title: 'Company',
      value: user?.company_id || 'Not assigned',
      icon: <Business sx={{ fontSize: 40, color: '#f59e0b' }} />,
      color: '#f59e0b',
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
          Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          Welcome back, <strong>{user?.first_name} {user?.last_name}</strong>! 👋
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {infoCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '120px',
                  height: '120px',
                  background: `linear-gradient(135deg, ${card.color}20 0%, ${card.color}08 100%)`,
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: `linear-gradient(90deg, ${card.color} 0%, ${card.color}80 100%)`,
                },
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${card.color}15 0%, ${card.color}08 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {card.icon}
                  </Box>
                  {card.title === 'Status' && (
                    <Chip
                      label={user?.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        background: user?.is_active ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: '#ffffff',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      }}
                    />
                  )}
                </Box>
                <Typography 
                  color="text.secondary" 
                  gutterBottom 
                  sx={{ 
                    fontSize: '0.8125rem', 
                    fontWeight: 600, 
                    mb: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {card.title}
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: card.color,
                    wordBreak: 'break-word',
                    lineHeight: 1.2,
                  }}
                >
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
            Quick Start Guide
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
            Use the navigation menu on the left to access different sections of the system.
            You can manage companies, users, services, agreements, and transactions from the dashboard.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="Companies" 
              sx={{ 
                background: 'rgba(37, 99, 235, 0.1)', 
                color: '#2563eb',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Agreements" 
              sx={{ 
                background: 'rgba(37, 99, 235, 0.1)', 
                color: '#2563eb',
                fontWeight: 600,
              }} 
            />
            <Chip 
              label="Transactions" 
              sx={{ 
                background: 'rgba(37, 99, 235, 0.1)', 
                color: '#2563eb',
                fontWeight: 600,
              }} 
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;